import { createTheme } from '@mui/material';

import colors from '@/utils/colors';

declare module '@mui/material/styles' {
  interface Palette {
    fileInput: Palette['primary'];
    disabled?: Palette['primary'];
    avatar?: Palette['primary'];
    hint?: Palette['primary'];
    asset?: Palette['primary'];
    selectedRow?: Palette['primary'];
    notification?: Palette['primary'];
  }
  interface PaletteOptions {
    fileInput: PaletteOptions['primary'];
    disabled?: PaletteOptions['primary'];
    avatar?: PaletteOptions['primary'];
    hint?: PaletteOptions['primary'];
    asset?: PaletteOptions['primary'];
    selectedRow?: PaletteOptions['primary'];
    notification?: PaletteOptions['primary'];
  }
}

const theme = createTheme({
  typography: {
    fontFamily: 'inherit',
    h1: {
      fontSize: '42px',
      fontWeight: 'bold',
      lineHeight: '64px',
    },
    h2: {
      fontSize: '36px',
      fontWeight: 'bold',
      lineHeight: '54px',
    },
    h3: {
      fontSize: '24px',
      fontWeight: '500',
      lineHeight: '48px',
    },
    h4: {
      fontSize: '24px',
      fontWeight: '400',
      lineHeight: '48px',
      color: '#28282F',
    },
    h5: {
      fontSize: '18px',
      fontWeight: 'bold',
      lineHeight: '22px',
    },
    h6: {
      fontSize: '24px',
      fontWeight: 'semibold',
      lineHeight: '32px',
    },
    body1: {
      fontSize: '16px',
      lineHeight: '20px',
      fontWeight: 500,
      color: '#52525B',
    },
    body2: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#52525B',
    },
    subtitle1: {
      fontSize: '12px',
      fontWeight: 600,
      color: 'black',
    },
    subtitle2: {
      fontSize: '14px',
      lineHeight: '14px',
      fontWeight: 600,
      textTransform: 'capitalize',
      color: 'rgba(0, 0, 0, 0.5)',
    },
    caption: {
      fontSize: '11px',
      lineHeight: '11px',
      textTransform: 'uppercase',
      color: 'rgba(0, 0, 0, 0.5)',
    },
    button: {
      textTransform: 'none',
      fontSize: '14px',
      lineHeight: '16.8px',
      borderRadius: '8px',
    },
  },
  palette: {
    primary: {
      main: colors.primary['500'],
      light: colors.primary['500'],
      contrastText: '#BFBFC9',
    },
    secondary: {
      main: '#FFFFFF',
      light: '#DDDDE7',
      contrastText: '#F0F0F5',
    },
    text: {
      primary: '#52525B',
      secondary: '#109AB5',
      disabled: '#BFBFC9',
    },
    error: {
      main: '#D80B00',
    },
    success: {
      main: '#C8ECE7',
      light: '#03423A',
    },
    fileInput: {
      main: '#E7FBFF',
      light: '#63636C',
    },
    disabled: {
      main: 'rgba(0, 0, 0, 0.26)',
    },
    avatar: {
      main: colors.primary['500'],
    },
    hint: {
      main: '#3AB6CE',
      light: 'color-mix(in oklch, var(--primary-100) 25%, transparent)',
      contrastText: '#FFE8B0',
    },
    info: {
      main: '#FFF7E7',
      light: '#BA7903',
      contrastText: '#8F2199',
    },
    asset: {
      main: '#0C0C0D',
      light: '#0C0C0DCC',
    },
    selectedRow: {
      main: 'color-mix(in oklch, var(--primary-100) 25%, transparent)',
    },
    notification: {
      main: '#F4C56A',
      contrastText: '#724D08',
    },
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          lineHeight: '16.8px',
          fontWeight: 500,
          color: '#52525B',
          height: '36px',
          borderColor: colors.border,
          padding: 0,
          borderRadius: '6px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '6px ',
          '& fieldset': {
            borderColor: colors.border,
          },
          '&.Mui-disabled': {
            backgroundColor: '#FFFFFF',
            color: '#BFBFC9',
            '& .MuiInputBase-input::placeholder': {
              color: '#8C8C96',
              opacity: 1,
            },
          },
        },
        input: {
          '&::placeholder': {
            color: '#8C8C96',
            opacity: 1,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
        },
        select: {
          lineHeight: '1.4375em',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

export default theme;
